import {useEffect} from 'react';
import PropTypes from 'prop-types';
import { TASK_ACTIONS, CONFIRMATION, DIRECT_TASK_ACTIONS, MODAL_SEVERITY } from "utils/constants";

import {connect} from 'react-redux';
import {loadTasks, addTaskToBatch, seamlessLoadTasks} from './myTasks.action';
import {actionFinished} from "./action/taskAction.action";
import {handleTask} from './action/handleTask.action';
import {
    getTasksForRender,
    getActionParameters,
    getDocumentEditedFlag,
    getUsersShowCommentOnApprove,
    getActiveBatch,
    getHandledTasksKeys,
    getUsersLoggedInData
} from '../store/application.reducers';
import {showNotification} from '../notifications/notification.action';
import * as logger from '../../utils/logger.function';
import {navigateBetweenTasks} from '../mytasks/action/navigateBetweenTasks.action';
import _ from "lodash";
import {useNavigate} from "react-router-dom";
import {useModalContext} from "components/modals/ModalContext";

/**
 * asynchronous part of the task action - showing the dialog, which will execute the action
 * synchronous part of action is inside redux action
 */
export const AsyncTaskActionExecutor = (props) => {
    const navigate = useNavigate();
    const modalContext = useModalContext();

    const {toggleModal} = modalContext || {};
    const closeDialogCallback = () => {
        props.actionFinished();
        // props.seamlessLoadTasks();
    }

    const actionParameters = props.actionParameters;

    /**
     * opening the modal is a state update on the host, so it cannot happen while rendering.
     * actionParameters is the redux action object, a stable reference until another action is
     * dispatched - so this runs once per requested action rather than on every re-render.
     */
    useEffect(() => {
        // nothing to show
        if (!actionParameters.type) {
            return;
        }

        let taskList = props.tasks.allTasks.map((task) => task.task);

        const tasksForAction = taskList
            .filter((task) => { // find those matching props.taskIds
                return actionParameters.taskIds.some((taskKeyWanted) => {
                    return taskKeyWanted === task.key;
                });
            });

        /**
         * sometimes, when user has dialog opened and navigates away for some time or changes context
         * the keys for task/history details expire, which then results in empty tasks for the popup
         * so detect this situation and rather hide the popup than check for invalid props inside each popup
         */
        if (tasksForAction.length === 0) {
            logger.debug("AsyncTaskActionExecutor", "no task to be shown for these keys: " + actionParameters.taskIds);
            props.actionFinished();
            return;
        }

        let isCurrentTaskHandled = _.findIndex(props.handledTasks, (id) => {
            return id === tasksForAction[0].key
        }) !== -1;


        let closeCallback = closeDialogCallback.bind(this);

        const popupProps = {
            closeCallback: closeCallback,
            handledTaskCallback: props.handleTask,
            translate: props.translate,
            tasks: tasksForAction,
            showNotification: props.showNotification,
            refreshTaskList: props.seamlessLoadTasks,
            taskDetails: props.taskDetails,
            isCurrentTaskHandled: isCurrentTaskHandled,
            navigate: navigate,
        };

        // according to type either show the modal or execute the action directly ..
        switch (actionParameters.type) {
            case TASK_ACTIONS.APPROVE:
                if (props.taskDetails && props.docEditedFlag && props.taskDetails.reapproveType === CONFIRMATION && props.taskDetails.skipRestart === true) {
                    toggleModal?.(TASK_ACTIONS.REAPPROVE, {...popupProps});
                } else {
                    const title = tasksForAction.length > 1
                        ? props.translate?.("popUp.approve.multipleApproveTitle")
                        : props.translate?.("popUp.approve.title");
                    toggleModal?.(TASK_ACTIONS.APPROVE, {...popupProps, showComments: props.showCommentsArea, title});
                }
                break;

            case TASK_ACTIONS.SEND_FOR_REVIEW:
                // the task leaves the user's list and comes back among the reviews they sent
                toggleModal?.(TASK_ACTIONS.SEND_FOR_REVIEW, {
                    ...popupProps,
                    handledTaskCallback: (taskKey, navigateTo) => {
                        props.handleTask(taskKey, navigateTo);
                        props.onSentForReview?.();
                    }
                });
                break;

            case TASK_ACTIONS.REVIEW:
                toggleModal?.(TASK_ACTIONS.REVIEW, {...popupProps});
                break;

            case TASK_ACTIONS.EMAIL:
                toggleModal?.(TASK_ACTIONS.EMAIL, {...popupProps, loggedInData: props.loggedInData});
                break;

            case TASK_ACTIONS.FORWARD:
                toggleModal?.(TASK_ACTIONS.FORWARD, {...popupProps});
                break;

            case TASK_ACTIONS.POSTPONE:
                toggleModal?.(TASK_ACTIONS.POSTPONE, {...popupProps});
                break;
            case TASK_ACTIONS.REJECT:
                toggleModal?.(TASK_ACTIONS.REJECT, {...popupProps});
                break;
            case TASK_ACTIONS.ADD_ATTACHMENT:
                toggleModal?.(TASK_ACTIONS.ADD_ATTACHMENT, {...popupProps});
                break;

            case TASK_ACTIONS.DELETE_ATTACHMENT:
                toggleModal?.(TASK_ACTIONS.DELETE_ATTACHMENT, {...popupProps, severity: MODAL_SEVERITY.WARNING});
                break;

            case TASK_ACTIONS.NEXT_TASK:
                const nextTaskId = props.navigateBetweenTasks(tasksForAction[0].key, true);
                navigate(nextTaskId, {replace: true});
                props.actionFinished();
                break;
            case TASK_ACTIONS.PREVIOUS_TASK:
                const previousTaskId = props.navigateBetweenTasks(tasksForAction[0].key, false);
                navigate(previousTaskId, {replace: true});
                props.actionFinished();
                break;

            case TASK_ACTIONS.APPROVE_BATCH:
                const batchID = props.activeBatch ? props.activeBatch.id : undefined;
                const version = props.activeBatch ? props.activeBatch.version : undefined;
                props.addTaskToBatch(batchID, version, tasksForAction[0].id, tasksForAction[0].key, navigate);
                props.actionFinished();
                break;

            case DIRECT_TASK_ACTIONS.APPROVE_IMMEDIATELY:
                //do nothing here - no modal needed
                break;

            default:
                console.error("unmapped action type: " + actionParameters.type);

        }

    }, [actionParameters]); // eslint-disable-line react-hooks/exhaustive-deps -- once per action, not per render

    return null;

}

AsyncTaskActionExecutor.propTypes = {
    translate: PropTypes.func,
    // reads the reviews again once a task is sent on review - only My tasks lists them
    onSentForReview: PropTypes.func,
    taskDetails: PropTypes.object,
    tasks: PropTypes.shape({
        allTasks: PropTypes.array
    }),
    actionParameters: PropTypes.shape({
        type: PropTypes.string,
        taskIds: PropTypes.array
    }),
    docEditedFlag: PropTypes.bool,
    showCommentsArea: PropTypes.bool,
    // the reducer copies the batch into an array when a task is added to it
    activeBatch: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    handledTasks: PropTypes.array,
    loggedInData: PropTypes.object,
    actionFinished: PropTypes.func,
    handleTask: PropTypes.func,
    showNotification: PropTypes.func,
    navigateBetweenTasks: PropTypes.func,
    addTaskToBatch: PropTypes.func,
    seamlessLoadTasks: PropTypes.func
};


const mapStateToProps = function (store) {
    return {
        tasks: getTasksForRender(store),
        actionParameters: getActionParameters(store),
        docEditedFlag: getDocumentEditedFlag(store),
        showCommentsArea: getUsersShowCommentOnApprove(store),
        activeBatch: getActiveBatch(store),
        handledTasks: getHandledTasksKeys(store),
        loggedInData: getUsersLoggedInData(store)
    };
};

const connected = connect(mapStateToProps, {
    actionFinished,
    handleTask,
    showNotification,
    loadTasks,
    navigateBetweenTasks,
    addTaskToBatch,
    seamlessLoadTasks
})(AsyncTaskActionExecutor);


export default connected;